import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PawPrint, LineChart, Stethoscope, ShieldCheck } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <PawPrint size={18} />
          </span>
          <span className="font-display text-lg font-semibold">
            Cat Kidney <span className="text-primary">Compass</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="ghost" data-testid="nav-login-button" className="rounded-full">
              Log in
            </Button>
          </Link>
          <Link to="/register">
            <Button data-testid="nav-register-button" className="rounded-full">
              Get started
            </Button>
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-medium tracking-wide uppercase text-primary bg-primary/10 px-3 py-1.5 rounded-full">
            IRIS 2023 · Feline CKD
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
            Understand your cat&apos;s kidneys,
            <span className="text-primary"> one lab at a time.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
            Cat Kidney Compass helps caretakers track feline lab results over time,
            computes the complete IRIS stage (with proteinuria and blood-pressure
            substaging), and suggests care strategies from the latest IRIS
            guidelines. Made for the humans who love these small, resilient creatures.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/signin">
              <Button size="lg" data-testid="hero-cta-register" className="rounded-full px-7">
                Start tracking free
              </Button>
            </Link>
            <Link to="/signin">
              <Button size="lg" variant="outline" data-testid="hero-cta-login" className="rounded-full px-7">
                I have an account
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 bg-primary/5 rounded-[3rem] blur-2xl" />
          <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="A cat looking peacefully to the side"
              className="w-full h-[520px] object-cover"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: <LineChart size={20} />,
            title: "Trend labs over time",
            body: "Log creatinine, SDMA, USG, UPC and blood pressure. See gentle line charts that show what's actually happening.",
          },
          {
            icon: <Stethoscope size={20} />,
            title: "Complete IRIS staging",
            body: "Once you have two lab sets, we compute Stage 1–4 plus proteinuria and hypertension substaging.",
          },
          {
            icon: <ShieldCheck size={20} />,
            title: "Guideline-based care",
            body: "Stage-tailored management strategies drawn from the IRIS 2023 CKD guidelines.",
          },
        ].map((f) => (
          <div key={f.title} className="card-soft p-6">
            <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              {f.icon}
            </span>
            <h3 className="font-display text-lg mt-4">{f.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
